import { Device } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

type UseRebootDeviceActionProps = {
    deviceId: string;
};

type UseRebootDeviceActionData = {
    rebootDevice: () => Promise<Device>;
    loading: boolean;
    error: string;
};

/**
 * Restarting a device. The device picks the command up the next time it
 * reports in, so this queues a reboot rather than performing one.
 *
 * No `isAllowed`/`reason`: the gate is the device settings page's own
 * `canEdit`, which it works out once for every action on the device and passes
 * down to the buttons.
 */
export function useRebootDeviceAction(
    props: UseRebootDeviceActionProps
): UseRebootDeviceActionData {
    const { deviceId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/devices/{deviceId}/actions/reboot` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'DEVICE_NOT_SETUP')) {
                return t('shared.errors.device_not_setup');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.devices
                .rebootDevice(deviceId)
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            // The command is queued, not done: the device the server hands back
            // carries it as a pending change, and the list has to show that.
            addOrUpdateAllDevicesCache(queryClient, device);

            UIToast.success(t('actions.reboot_device.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        rebootDevice: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
